import React, { useEffect, useState } from 'react';
import { useWorkOrderApi } from '../../../../store/api/hooks/workOrderApiHook';
import Button from '../../../../components/elements/button';
import { showLocalDate, showLocalTime } from '../../../../_common/functions';

interface Props {
    _ref: React.RefObject<HTMLDialogElement>;
    workOrderID?: number;
}

type formComponentTypes =
    | 'checkbox'
    | 'text-field'
    | 'yes-no-na'
    | 'number-field'
    | 'inspection'
    | 'multiple-choice'
    | 'amount'
    | 'checklist'
    | 'picture-file'
    | 'signature'
    | 'date'
    | 'time';

const ChecklistLogPreviewModal: React.FC<Props> = ({ _ref, workOrderID }) => {
    const workOrderAPI = useWorkOrderApi();
    const inputIDS: formComponentTypes[] = [
        'checkbox', 'text-field', 'yes-no-na', 'number-field', 'inspection',
        'multiple-choice', 'amount', 'checklist', 'picture-file', 'signature', 'date', 'time',
    ];

    const [selectedChecklist, setSelectedChecklist] = useState<any>(null);

    const getNewComponent = (formComponent: formComponentTypes, taskid: any) => {
        const ID = `field_${taskid}`;
        let newCard: any = { id: ID, component: formComponent };

        switch (formComponent) {
            case 'checkbox':
                return { ...newCard, type: 'checkbox', label: 'CheckBox', options: [] };
            case 'text-field':
                return { ...newCard, type: 'text', label: 'Text Field' };
            case 'yes-no-na':
                return {
                    ...newCard, type: 'radio', label: 'Yes, No, Not Applicable', options: [
                        { woTaskOptionID: '0', optionName: 'Yes' },
                        { woTaskOptionID: '1', optionName: 'No' },
                        { woTaskOptionID: '2', optionName: 'Not Applicable' },
                    ]
                };
            case 'number-field':
                return { ...newCard, type: 'number', label: 'Number Field' };
            case 'inspection':
                return {
                    ...newCard, type: 'radio', label: 'Inspection', options: [
                        { woTaskOptionID: '0', optionName: 'Pass' },
                        { woTaskOptionID: '1', optionName: 'Fail' },
                        { woTaskOptionID: '2', optionName: 'Flag' },
                    ]
                };
            case 'multiple-choice':
                return { ...newCard, type: 'radio', label: 'Multiple Choice', options: [] };
            case 'amount':
                return { ...newCard, type: 'number', label: 'Amount' };
            case 'checklist':
                return { ...newCard, type: 'checkbox', label: 'Checklist', options: [] };
            case 'picture-file':
                return { ...newCard, type: 'file', label: 'Picture/File', multiple: true };
            case 'signature':
                return { ...newCard, type: 'signature', label: 'Signature' };
            case 'date':
                return { ...newCard, type: 'date', label: 'Date' };
            case 'time':
                return { ...newCard, type: 'time', label: 'Time' };
            default:
                return null;
        }
    };

    const getFormComponent = (card: any) => {
        const commonClasses = "flex gap-4 items-center border-b border-gray-200 py-4 px-4 min-h-[80px]";
        console.log(card.type);
        switch (card.type) {
            case 'checkbox':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <div className="flex flex-wrap gap-4">
                            {card.options?.map((option: any, index: number) => (
                                <label key={index} className="flex gap-2">
                                    <input type="checkbox" value={option.woTaskOptionID} disabled checked={option?.isChecked} />
                                    <span>{option.optionName}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );
            case 'text':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <input type="text" disabled defaultValue={card?.value} className="border border-[#CECECE] text-[0.938rem] px-2 py-1 h-[2rem] w-full" />
                    </div>
                );
            case 'radio':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <div className="flex flex-wrap gap-4">
                            {card.options.map((option: any, index: number) => (
                                <label key={`${card.id}-${index}`} className="custom-dx-radio-box">
                                    <input type="radio" disabled checked={option.woTaskOptionID == card.value} />
                                    <span className='ml-2'>{option.optionName}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );
            case 'number':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <input type="number" disabled defaultValue={card.value} className="border border-[#CECECE] px-2 py-1 h-[2rem] w-full" />
                    </div>
                );
            case 'file':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <div className="flex flex-wrap gap-4">
                            {card.files?.length > 0 ? (
                                card.files.map((fileUrl: string, i: number) => (
                                    <a
                                        key={i}
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block border p-1 rounded"
                                    >
                                        <img
                                            src={fileUrl}
                                            alt={`File ${i}`}
                                            className="w-[100px] h-[100px] object-cover"
                                        />
                                    </a>
                                ))
                            ) : (
                                <span className="text-gray-400">No file uploaded</span>
                            )}
                        </div>
                    </div>
                );

            case 'signature':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        {card.files?.length > 0 ? (
                            <img
                                src={card.files[0]}
                                alt="Signature"
                                className="w-[250px] h-[80px] border object-contain"
                            />
                        ) : (
                            <span className="text-gray-400">No signature uploaded</span>
                        )}
                    </div>
                );

            case 'date':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <input type="text" disabled value={card.value || ''} className="border border-[#CECECE] px-2 py-1 h-[2rem] w-full" />
                    </div>
                );
            case 'time':
                return (
                    <div className={commonClasses}>
                        <div className="w-[400px] text-[#767676] text-[15px]">{card.label}</div>
                        <input type="text" disabled value={card.value || ''} className="border border-[#CECECE] px-2 py-1 h-[2rem] w-full" />
                    </div>
                );
            case 'checklist':
            case 'multiple-choice':
                console.log('here i am', card.options);

                return (
                    <div className="flex gap-4 items-start border-b border-gray-200 py-4 px-4" style={{ minHeight: `80px` }}>
                        <div style={{ minWidth: 400, maxWidth: 400, width: 400 }}>
                            <label style={{ fontSize: `15px`, color: `#767676` }}>{card.label}</label>
                        </div>
                        <div className="flex flex-col gap-2">
                            {card.options?.map((option: any, index: number) => (
                                <label className="flex gap-2 items-center custom-dx-radio" key={`${card.id}-${index}`}>
                                    <input
                                        type="radio"
                                        disabled
                                        checked={option.isChecked === true}
                                    />
                                    <span className='ml-2'>{option.optionName}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };


    const init_page_date = async () => {
        if (!workOrderID) return;

        workOrderAPI.GetWOTasks(
            { workOrderID },
            (resp: any) => {
                if (resp?.tasks?.length > 0) {
                    const files = resp.tasksFiles || [];

                    const firstChecklist = resp.tasks[0].checkListID;
                    const checklistTasks = resp.tasks.filter((t: any) => t.checkListID === firstChecklist);

                    const cards = checklistTasks.map((task: any) => {
                        const type = inputIDS[task.inputTypeID - 1];
                        const card = getNewComponent(type, task.woTaskID);
                        card.label = task.inputLabel;

                        if (['yes-no-na', 'inspection'].includes(type)) {
                            card.options = getNewComponent(type, task.woTaskID).options;
                            card.value = type === 'inspection' ? task.inspectionStatus : task.yesNoOption;
                        } else if (['checkbox', 'checklist'].includes(type)) {
                            card.options = task.options?.map((opt: any) => ({
                                ...opt,
                                isChecked: !!opt.isChecked,
                            })) || [];
                            card.value = '';
                        } else if (type === 'multiple-choice') {
                            card.options = task.options || [];
                            card.value = task.options?.find((opt: any) => opt.isChecked)?.woTaskOptionID || '';
                        } else if (type === 'time') {
                            card.value = showLocalTime(task.dateValue);
                        } else if (type === 'date') {
                            card.value = showLocalDate(task.dateValue);
                        } else {
                            card.value = task.textResponse || task.numberValue || task.amount || task.dateValue || '';
                        }

                        // ✅ Attach file or signature preview
                        if (['picture-file', 'signature'].includes(type)) {
                            card.files = files
                                .filter((f: any) => f.woTaskID === task.woTaskID && f.inputTypeID === task.inputTypeID)
                                .map((f: any) => f.fullFilePath);
                        }

                        return card;
                    });

                    setSelectedChecklist({ checkListTitle: checklistTasks[0].checkListTitle, tasks: cards });
                }
            },
            (error: any) => console.error("Error loading tasks:", error)
        );
    };


    const closeModal = () => _ref.current?.close();

    useEffect(() => {
        if (workOrderID) init_page_date();
    }, [workOrderID]);

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box max-w-[90%] w-[50rem] p-0 flex flex-col h-[90vh]">
                {/* Header */}
                <div className="flex justify-between items-center px-4 py-2 border-b border-gray-300 bg-white sticky top-0 z-10">
                    <h4 className="font-semibold text-[0.938rem]">Task of Checklist Preview</h4>
                    <button onClick={closeModal} className="text-xl font-bold text-gray-500 hover:text-black">×</button>
                </div>

                {/* Scrollable Body */}
                <div className="flex-1 overflow-y-auto px-2">
                    {selectedChecklist ? (
                        <>
                            <h4 className="font-semibold text-[0.938rem] my-2 px-4 py-2 bg-gray-200">
                                Tasks for Checklist: {selectedChecklist.checkListTitle}
                            </h4>
                            <form>
                                {selectedChecklist.tasks.map((card: any, index: number) => (
                                    <div key={index}>{getFormComponent(card)}</div>
                                ))}
                            </form>
                        </>
                    ) : (
                        <p className="text-center py-8 text-gray-600">No Checklist Found</p>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-300 bg-white sticky bottom-0 z-10 flex justify-end">
                    <Button type="button" theme="secondary" onClick={closeModal}>
                        Close
                    </Button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop" onClick={closeModal}></form>
        </dialog>
    );
};

export default ChecklistLogPreviewModal;
